import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Share2 } from 'lucide-react';

import { formatNumber } from '../../../components/design-system/admin';
import { RetailerNetworkSummary, useNetworkApi } from '../../../common/hooks/use-network-api.hook';

/**
 * One quiet line above the catalogue: how much of it is already in the
 * Ardealy Network, and the way to the network page.
 *
 * Says nothing when there is nothing to say (no links, no matches, nothing
 * to add) or when the network cannot be read — a catalogue page must never
 * show an error for a feature it is only pointing at.
 */
export const NetworkCatalogNudge: React.FC = () => {
    const { t } = useTranslation();
    const api = useNetworkApi();
    const [summary, setSummary] = useState<RetailerNetworkSummary | null>(null);

    useEffect(() => {
        let live = true;
        api.summary()
            .then((s) => live && setSummary(s))
            .catch(() => live && setSummary(null));
        return () => {
            live = false;
        };
    }, [api]);

    if (!summary) return null;
    const linked = summary.linked.variants;
    if (!linked && !summary.suggested && !summary.available) return null;

    const text = linked
        ? t('NETWORK_RETAILER.NUDGE.LINKED', { count: linked, formatted: formatNumber(linked) })
        : summary.suggested
          ? t('NETWORK_RETAILER.NUDGE.SUGGESTED', { count: summary.suggested, formatted: formatNumber(summary.suggested) })
          : t('NETWORK_RETAILER.NUDGE.AVAILABLE', { count: summary.available, formatted: formatNumber(summary.available) });

    return (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border bg-card px-4 py-2.5 text-sm" data-testid="network-catalog-nudge">
            <span className="flex items-center gap-2 text-muted-foreground">
                <Share2 className="size-4 shrink-0" aria-hidden />
                <span>
                    {text}
                    {linked > 0 && summary.suggested > 0 ? ` · ${t('NETWORK_RETAILER.NUDGE.AND_SUGGESTED', { count: summary.suggested })}` : ''}
                </span>
            </span>
            <Link to={summary.suggested ? '/products/network?tab=matches' : '/products/network?tab=browse'} className="text-sm font-medium hover:underline">
                {t('NETWORK_RETAILER.NUDGE.OPEN')}
            </Link>
        </div>
    );
};
